/**
 * RRWebRecorder — Wraps rrweb's record() to capture DOM mutations,
 * mouse events, scroll, and input for streaming to the vision backend.
 *
 * Masks sensitive inputs (passwords, emails, phone numbers).
 * Excludes the widget's own DOM (#holostaff-widget-root).
 * Full snapshot every 30s for replay drift correction.
 */

import { record } from 'rrweb'
import type { eventWithTime } from '@rrweb/types'

const CHECKOUT_INTERVAL_MS = 30_000

export class RRWebRecorder {
  /** Called for each rrweb event. Wire this to the WebSocket client. */
  onEvent: ((event: eventWithTime) => void) | null = null

  private stopFn: (() => void) | null = null
  private _recording = false
  private _paused = false

  get isRecording(): boolean {
    return this._recording
  }

  get isPaused(): boolean {
    return this._paused
  }

  /** Start recording the host page. */
  start(): void {
    if (this._recording) return

    const stop = record({
      emit: (event: eventWithTime) => {
        if (this._paused) return
        this.onEvent?.(event)
      },
      checkoutEveryNms: CHECKOUT_INTERVAL_MS,
      // Block the widget itself, credit card fields, and elements marked with data-hs-no-record
      blockClass: 'hs-no-record',
      blockSelector: '#holostaff-widget-root, [autocomplete^="cc-"], [data-hs-no-record]',
      // Mask sensitive input types + elements marked with data-hs-mask
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
      },
      maskTextSelector: '[data-hs-mask]',
    })

    if (stop) {
      this.stopFn = stop
      this._recording = true
      this._paused = false
      console.log('[RRWebRecorder] Started recording')
    } else {
      console.warn('[RRWebRecorder] record() returned undefined — recording may have failed')
    }
  }

  /** Stop recording and release resources. */
  stop(): void {
    if (!this._recording) return
    this.stopFn?.()
    this.stopFn = null
    this._recording = false
    this._paused = false
    console.log('[RRWebRecorder] Stopped recording')
  }

  /** Pause event emission (recording continues internally but events are discarded). */
  pause(): void {
    if (!this._recording || this._paused) return
    this._paused = true
    console.log('[RRWebRecorder] Paused')
  }

  /** Resume event emission. */
  resume(): void {
    if (!this._recording || !this._paused) return
    this._paused = false
    // Take a fresh full snapshot so the replay can resync
    record.takeFullSnapshot()
    console.log('[RRWebRecorder] Resumed (full snapshot taken)')
  }
}
